import {
  stashAgentDraft,
  type AgentDraft,
  type AgentDraftGoal,
  type AgentDraftVariable,
} from "@/shared/domain/agents";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import { CreateAgentConfirmation } from "./CreateAgentConfirmation";

// ---------------------------------------------------------------------------
// Input type
//
// `createClientTool` casts raw LLM JSON to this shape with `params as T`,
// so nothing here is statically verified at runtime. Scalars are typed
// best-effort (LLMs respect string types in practice); collection fields
// are `unknown[]` because the LLM may emit goals or variables with
// missing / wrong-typed nested fields, and the normalize* functions
// below are the only validation layer between that input and the
// stashed draft.
// ---------------------------------------------------------------------------

interface ClientCreateAgentInput {
  name?: string;
  description?: string;
  model_profile?: string;
  kickoff_message?: string;
  goals?: unknown[];
  variables?: unknown[];
}

// ---------------------------------------------------------------------------
// JSON Schema
//
// Goals are a single flat item shape with all variant-specific fields
// optional. The LLM picks which fields to populate based on the value it
// supplies for `goal_type`, and `normalizeGoals` discriminates server-
// side (browser-side, here) by reading `goal_type` directly. Sibling
// tools (`navigate`, `createEvents`) follow the same flat-and-
// discriminate convention; an earlier draft used `oneOf` keyed on
// `goal_type` but no test exercises Bedrock's Converse tool-schema
// validator against `oneOf` in array items, and a rejection there would
// fail the entire thread request (not just this tool).
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    name: {
      type: "string",
      description:
        "Short human-readable name for the agent (required). Appears in the agents list.",
    },
    description: {
      type: "string",
      description:
        "Longer prose describing what the agent does and when teammates should invoke it.",
    },
    model_profile: {
      type: "string",
      description:
        "Optional model-profile id (e.g. 'standard', 'advanced'). Omit to use the org default.",
    },
    kickoff_message: {
      type: "string",
      description:
        "Optional user message seeded as the agent's first turn. Reference variables with {{name}}.",
    },
    goals: {
      type: "array",
      description:
        "Goals the agent must accomplish on each invocation. At least one goal or a kickoff_message is required for the agent to do anything; otherwise the editor surfaces a 'needs a goal or kickoff' warning.",
      items: {
        type: "object",
        description:
          "One goal. Set `goal_type` to either 'dataset_summary' (then optionally `summary_format_spec_prompt`) or 'dataset_triage' (then provide `label_vocabulary`). Other fields are ignored for the chosen type.",
        properties: {
          goal_type: {
            type: "string",
            enum: ["dataset_summary", "dataset_triage"],
            description:
              "Which goal variant. `dataset_summary` makes the agent write a summary of the dataset; `dataset_triage` makes it pick one label from a vocabulary.",
          },
          summary_format_spec_prompt: {
            type: "string",
            description:
              "For `dataset_summary` only: optional natural-language description of what the summary should include / emphasize.",
          },
          label_vocabulary: {
            type: "object",
            description:
              "For `dataset_triage` only: a map from label name to a description that tells the agent when to apply it. The agent picks one label per dataset.",
            additionalProperties: { type: "string" },
          },
        },
        required: ["goal_type"],
      },
    },
    variables: {
      type: "array",
      description:
        "Variables referenced via {{name}} placeholders in kickoff_message or goal text. Only declare variables you actually reference; extras are dropped silently by the editor.",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description:
              "Variable name matching ^[a-zA-Z_][a-zA-Z0-9_.]*$. Used inside {{...}}.",
          },
          description: {
            type: "string",
            description:
              "Human-readable explanation shown next to the variable's input control at invoke time.",
          },
          default: {
            type: "string",
            description:
              "Default value substituted when the caller omits the variable.",
          },
        },
        required: ["name"],
      },
    },
  },
  required: ["name"],
};

// ---------------------------------------------------------------------------
// Coercion — tool input → AgentDraft
//
// This is the one validation seam between LLM-emitted JSON and the
// stashed draft the editor reads back. Treat every field as untrusted
// even where the TypeScript annotation suggests otherwise.
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function trimOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function normalizeGoals(
  goals: unknown[] | undefined,
): AgentDraftGoal[] | undefined {
  if (!Array.isArray(goals) || goals.length === 0) {
    return undefined;
  }
  const normalized: AgentDraftGoal[] = [];
  for (const raw of goals) {
    if (!isPlainObject(raw)) {
      continue;
    }
    const goalType = raw.goal_type;
    if (goalType === "dataset_summary") {
      const prompt = trimOrUndefined(raw.summary_format_spec_prompt);
      normalized.push(
        prompt === undefined
          ? { goal_type: "dataset_summary" }
          : {
              goal_type: "dataset_summary",
              summary_format_spec_prompt: prompt,
            },
      );
    } else if (goalType === "dataset_triage") {
      const vocab: Record<string, string> = {};
      const rawVocab = raw.label_vocabulary;
      if (isPlainObject(rawVocab)) {
        for (const [label, description] of Object.entries(rawVocab)) {
          const trimmedLabel = label.trim();
          if (trimmedLabel === "" || typeof description !== "string") {
            continue;
          }
          vocab[trimmedLabel] = description;
        }
      }
      // Drop a triage goal whose vocabulary ended up empty after
      // filtering — schema marks `label_vocabulary` as required for the
      // type, and the editor's validator would block save until the
      // user adds at least one row. Better to surface nothing than a
      // half-formed goal that looks like it came from the model.
      if (Object.keys(vocab).length > 0) {
        normalized.push({
          goal_type: "dataset_triage",
          label_vocabulary: vocab,
        });
      }
    }
    // Unknown goal_type values are dropped silently so a future server
    // goal we don't know about can't wedge the editor.
  }
  return normalized.length === 0 ? undefined : normalized;
}

function normalizeVariables(
  variables: unknown[] | undefined,
): AgentDraftVariable[] | undefined {
  if (!Array.isArray(variables) || variables.length === 0) {
    return undefined;
  }
  const normalized: AgentDraftVariable[] = [];
  for (const raw of variables) {
    if (!isPlainObject(raw)) {
      continue;
    }
    const name = trimOrUndefined(raw.name);
    if (name === undefined) {
      continue;
    }
    const entry: AgentDraftVariable = { name };
    const description = trimOrUndefined(raw.description);
    if (description !== undefined) {
      entry.description = description;
    }
    // `default` is preserved verbatim (including empty / whitespace-only
    // strings) because an empty default is a meaningful choice — the LLM
    // is telling us "this variable has no default; force the caller to
    // supply one." Trimming here would collapse that into "no default
    // declared", which is a different (less strict) contract.
    if (typeof raw.default === "string") {
      entry.default = raw.default;
    }
    normalized.push(entry);
  }
  return normalized.length === 0 ? undefined : normalized;
}

export function coerceToAgentDraft(
  input: ClientCreateAgentInput,
): AgentDraft | null {
  const name = trimOrUndefined(input.name);
  if (name === undefined) {
    return null;
  }
  const draft: AgentDraft = { name };
  const description = trimOrUndefined(input.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const modelProfile = trimOrUndefined(input.model_profile);
  if (modelProfile !== undefined) {
    draft.model_profile = modelProfile;
  }
  if (
    typeof input.kickoff_message === "string" &&
    input.kickoff_message.trim() !== ""
  ) {
    // Preserve interior whitespace verbatim — kickoff messages routinely
    // span lines, and `\n\n` between paragraphs is meaningful. The trim
    // check above only filters out the all-whitespace case, which
    // produces a useless empty user-turn in the editor.
    draft.kickoff_message = input.kickoff_message;
  }
  const goals = normalizeGoals(input.goals);
  if (goals !== undefined) {
    draft.goals = goals;
  }
  const variables = normalizeVariables(input.variables);
  if (variables !== undefined) {
    draft.variables = variables;
  }
  return draft;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

function execute(
  input: ClientCreateAgentInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  const draft = coerceToAgentDraft(input);
  if (draft === null) {
    return toolError("Agent draft requires a non-empty 'name'.");
  }
  stashAgentDraft(draft);
  deps.goto.navigateTo(Goto.route.CreateAgent);
  return toolSuccess("opened_agent_editor_with_draft", {
    navigated_to: Goto.route.CreateAgent,
    name: draft.name,
  });
}

// ---------------------------------------------------------------------------
// Tool module
// ---------------------------------------------------------------------------

export const tool = createClientTool<ClientCreateAgentInput>({
  name: "client_create_agent",
  description:
    "Open the Agent editor pre-filled with a drafted Agent template based on the current conversation. " +
    "Use this when the user asks to turn the current agentThread (or a stated workflow) into a re-usable Agent, " +
    "or when they explicitly ask you to draft / create an Agent. " +
    "Provide a 'name' and any description, model_profile, kickoff_message, goals, or variables you can infer. " +
    "The user lands in the editor to review, refine, and save — this tool does NOT persist the Agent.",
  input_schema: inputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open agent editor with draft",
  displayDescription:
    "Opens the Agent editor pre-filled with a drafted Agent template. The agent isn't saved until you click Create in the editor.",
  ConfirmationContent: CreateAgentConfirmation,
});
